/**
 * DonationBanner (T35.7.2 Part A) — the pinned "support development" banner.
 *
 * Owned by the subscriptions module. Uses `DonationDialog` to record a general donation
 * via `/api/subscriptions/` (no idea, `type: 'donation'`).
 *
 * Pinned above the bottom nav on the Feedback tab only (mounted by
 * `FeatureIdeasPage`). "Support" opens the donation dialog; the dialog owns the
 * one-and-only success message (the universal thank-you). On a successful
 * donation the banner simply DISMISSES for the session — it has done its nudge,
 * and there is NO second thank-you on the banner (anti-drift, T35.7.2.2). The ✕
 * also dismisses it for the SESSION (it re-shows on the next app load) — a
 * module-level flag, not persisted.
 *
 * Mode-independent (T39): Feedback does not consume App mode or location
 * context. The banner shows on the Feedback tab (where it's mounted) in any App
 * mode; its only gate is the session dismissal. Token-only (CP5); mobile-first;
 * ≥44px targets.
 */

import { useState, type CSSProperties } from 'react';
import { DonationDialog } from './DonationDialog';

// Session-scoped dismissal: a plain module flag, reset on a full reload.
// Deliberately NOT persisted to storage.
let dismissedThisSession = false;

// Exported for DonationBanner.test.tsx — the test asserts on this source
// object rather than reading via CSSOM (happy-dom drops nested calc/var).
export const bannerStyle: CSSProperties = {
  position: 'fixed',
  left: 'var(--space-3)',
  right: 'var(--space-3)',
  bottom: 'calc(var(--app-bottomnav-height, 3.5rem) + var(--space-2))',
  zIndex: 'var(--z-banner)' as unknown as number,
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  border: '0.0625rem solid var(--color-accent-secondary)',
  borderRadius: 'var(--radius-lg)',
  backgroundColor: 'var(--color-accent-subtle)',
  boxShadow: 'var(--shadow-lg)',
};

const artStyle: CSSProperties = {
  flex: '0 0 auto',
  width: '2rem',
  height: '2rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-bg-raised)',
  border: '0.0625rem solid var(--color-accent-secondary)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-base)',
};

const mainStyle: CSSProperties = { flex: 1, minWidth: 0 };

const titleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
  lineHeight: 1.3,
};

const descStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.35,
};

const ctaStyle: CSSProperties = {
  flex: '0 0 auto',
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const closeStyle: CSSProperties = {
  flex: '0 0 auto',
  alignSelf: 'flex-start',
  minWidth: '2.75rem',
  minHeight: '2.75rem',
  margin: 'calc(var(--space-2) * -1) calc(var(--space-2) * -1) calc(var(--space-2) * -1) 0',
  border: 0,
  background: 'transparent',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-sm)',
  lineHeight: 1,
  cursor: 'pointer',
  borderRadius: 'var(--radius-pill)',
  display: 'grid',
  placeItems: 'center',
};

export function DonationBanner(): JSX.Element | null {
  const [dismissed, setDismissed] = useState(dismissedThisSession);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogBusy, setDialogBusy] = useState(false);
  // Set when a donation succeeds. The dialog stays open on its (one-and-only)
  // thank-you; the banner dismisses only once the user closes that dialog — so
  // it never unmounts the success message out from under them.
  const [donated, setDonated] = useState(false);

  function dismiss(): void {
    dismissedThisSession = true;
    setDismissed(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
    // A successful donation retires the banner for the session (no second
    // thank-you here — the dialog already showed the only one).
    if (donated) dismiss();
  }

  // T39 — no App-mode / location guard. Gone for the session once dismissed.
  if (dismissed) return null;

  return (
    <>
      <div style={bannerStyle} role="complementary" aria-label="Support development" data-testid="dev-support-banner">
        <div style={artStyle} aria-hidden="true">
          💜
        </div>
        <div style={mainStyle}>
          <div style={titleStyle}>Enjoying Shopista?</div>
          <div style={descStyle}>A dollar or two helps us keep building it.</div>
        </div>
        <button type="button" style={ctaStyle} onClick={() => setDialogOpen(true)} data-testid="dev-support-cta">
          Support
        </button>
        <button type="button" style={closeStyle} onClick={dismiss} aria-label="Dismiss" data-testid="dev-support-dismiss">
          ✕
        </button>
      </div>

      <DonationDialog
        open={dialogOpen}
        busy={dialogBusy}
        setBusy={setDialogBusy}
        onClose={closeDialog}
        onConfirmed={() => {
          // Successful donation — the dialog is showing the one-and-only thank-you.
          // Mark it so the banner retires for the session once the dialog closes.
          // There is NO second thank-you on the banner (anti-drift, T35.7.2.2).
          setDonated(true);
        }}
      />
    </>
  );
}
